import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FPNDetails } from 'src/types/fpn/fpnDetails.type';
import { logger } from 'src/utils/logger';
import { fetchAGMVersionsAttributeOrder, updateAGMVersionsAttributeOrder } from '../../services/api/agmService';
import {
  fetchFPNDetailsAPI,
  fetchProductLineDetailsByIdAPI,
  fetchRPNDetailsAPI,
} from '../../services/api/productDetailsService';
import {
  AgmVersionAttributeOrderApiResponse,
  RpnAssortment,
  RPNDetails,
  RpnVersion,
  Skus,
} from '../../types/rpn/rpnDetails.type';
import { selectRpnIdToAliasMap, convertProgramPredecessorValue } from '../selectors/productHierarchySelectors';

/**
 * Product Details Slice
 * Manages individual entity details and navigation context for Product Lines, RPNs, and FPNs
 * This slice handles the detail pages while productHierarchySlice handles the grid view
 */

// ============================================================================
// State Interfaces
// ============================================================================

interface NavigationContext {
  productLineId: number | null;
  rpnId: number | null;
  fpnId: number | null;
}

interface LoadingState {
  productLine: boolean;
  rpn: boolean;
  fpn: boolean;
  agmVersionsAttributeOrder: boolean;
}

interface ProductDetailsSliceState {
  // Current navigation context - where the user is
  currentContext: NavigationContext;

  // Cached entity data (normalized by composite keys)
  productLines: Record<number, unknown>; // key: productLineId
  rpns: Record<string, RPNDetails>; // key: `${productLineId}-${rpnId}`
  fpns: Record<string, FPNDetails>; // key: `${productLineId}-${rpnId}-${fpnId}`

  // AGM Versions Attribute Order data (keyed by `${productLineId}-${rpnId}`)
  agmVersionsAttributeOrder: Record<string, AgmVersionAttributeOrderApiResponse>;

  // Loading states
  loading: LoadingState;

  // Error state
  error: string | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ProductDetailsSliceState = {
  currentContext: {
    productLineId: null,
    rpnId: null,
    fpnId: null,
  },
  productLines: {},
  rpns: {},
  fpns: {},
  agmVersionsAttributeOrder: {},
  loading: {
    productLine: false,
    rpn: false,
    fpn: false,
    agmVersionsAttributeOrder: false,
  },
  error: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches product line details by ID
 */
export const fetchProductLineDetails = createAsyncThunk(
  'productDetails/fetchProductLineDetails',
  async (productLineId: number, { rejectWithValue }) => {
    try {
      const data = await fetchProductLineDetailsByIdAPI(productLineId);
      return { productLineId, data };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch product line details');
    }
  }
);

/**
 * Fetches RPN details by product line ID and RPN ID
 * Converts program_predecessor from RPN ID to alias using the minimal hierarchy data
 */
export const fetchRPNDetails = createAsyncThunk(
  'productDetails/fetchRPNDetails',
  async ({ productLineId, rpnId }: { productLineId: number; rpnId: number }, { rejectWithValue, getState }) => {
    try {
      const data = await fetchRPNDetailsAPI(productLineId, rpnId);
      const rpnData = data as RPNDetails;

      // Convert program_predecessor from RPN ID to alias
      try {
        const state = getState() as { productHierarchy: { productHierarchyMinimal: { data: unknown[] } } };
        const rpnIdToAliasMap = selectRpnIdToAliasMap(state as never);

        if (rpnData.rpn_overview?.rpn_program_overview?.program_predecessor) {
          const convertedPredecessor = convertProgramPredecessorValue(
            rpnData.rpn_overview.rpn_program_overview.program_predecessor,
            rpnIdToAliasMap
          );
          rpnData.rpn_overview.rpn_program_overview.program_predecessor = convertedPredecessor;
        }
      } catch (error) {
        logger.error('Failed to convert program_predecessor', error as Error);
        // Continue with original value - conversion is a display enhancement, not critical
      }

      return { productLineId, rpnId, data: rpnData };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch RPN details');
    }
  }
);

/**
 * Fetches FPN details by product line ID, RPN ID, and FPN ID
 */
export const fetchFPNDetails = createAsyncThunk(
  'productDetails/fetchFPNDetails',
  async (
    { productLineId, rpnId, fpnId }: { productLineId: number; rpnId: number; fpnId: number },
    { rejectWithValue }
  ) => {
    try {
      const data = await fetchFPNDetailsAPI(productLineId, rpnId, fpnId);
      return { productLineId, rpnId, fpnId, data: data as FPNDetails };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch FPN details');
    }
  }
);

/**
 * Fetches AGM Versions Attribute Order by product line ID and RPN ID
 *
 */
export const fetchAGMVersionsAttributeOrderThunk = createAsyncThunk(
  'productDetails/fetchAGMVersionsAttributeOrder',
  async ({ productLineId, rpnId }: { productLineId: number; rpnId: number }, { rejectWithValue }) => {
    try {
      const data = await fetchAGMVersionsAttributeOrder(productLineId, rpnId);
      return { productLineId, rpnId, data };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch AGM Versions Attribute Order');
    }
  }
);

/**
 * Updates AGM Versions Attribute Order for a specific product line and RPN
 *
 * Calls the updateAGMAttributeOrder GraphQL mutation to save the new attribute order.
 */
export const updateAGMVersionsAttributeOrderThunk = createAsyncThunk(
  'productDetails/updateAGMVersionsAttributeOrder',
  async (
    {
      productLineId,
      rpnId,
      attributeOrders,
    }: { productLineId: number; rpnId: number; attributeOrders: { attribute_type: string; order: number }[] },
    { rejectWithValue }
  ) => {
    try {
      const result = await updateAGMVersionsAttributeOrder(rpnId, attributeOrders);
      return { productLineId, rpnId, result };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update AGM Versions Attribute Order');
    }
  }
);

// ============================================================================
// Slice Definition
// ============================================================================

const productDetailsSlice = createSlice({
  name: 'productDetails',
  initialState,
  reducers: {
    /**
     * Sets the current navigation context
     */
    setNavigationContext: (state, action: PayloadAction<NavigationContext>) => {
      state.currentContext = action.payload;
    },

    /**
     * Navigates to a product line
     */
    navigateToProductLine: (state, action: PayloadAction<number>) => {
      state.currentContext = {
        productLineId: action.payload,
        rpnId: null,
        fpnId: null,
      };
    },

    /**
     * Navigates to an RPN
     */
    navigateToRPN: (state, action: PayloadAction<{ productLineId: number; rpnId: number }>) => {
      state.currentContext = {
        productLineId: action.payload.productLineId,
        rpnId: action.payload.rpnId,
        fpnId: null,
      };
    },

    /**
     * Navigates to an FPN
     */
    navigateToFPN: (state, action: PayloadAction<{ productLineId: number; rpnId: number; fpnId: number }>) => {
      state.currentContext = {
        productLineId: action.payload.productLineId,
        rpnId: action.payload.rpnId,
        fpnId: action.payload.fpnId,
      };
    },

    /**
     * Clears error state
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Resets the slice to initial state
     */
    reset: () => initialState,

    // ========================================================================
    // Targeted Update Actions (for updating specific AGM data without full refetch)
    // ========================================================================

    /**
     * Updates only the rpn_versions array within an RPN's agm_data without triggering full refetch
     * This allows child components to update Redux store after local mutations
     */
    updateRPNVersions: (
      state,
      action: PayloadAction<{ productLineId: number; rpnId: number; versions: RpnVersion[] }>
    ) => {
      const key = `${action.payload.productLineId}-${action.payload.rpnId}`;
      if (!state.rpns[key]) {
        logger.warn(`[productDetailsSlice] Attempted to update versions for non-existent RPN: ${key}`);
        return;
      }
      if (state.rpns[key].agm_data) {
        state.rpns[key].agm_data.rpn_versions = action.payload.versions;
      }
    },

    /**
     * Updates only the rpn_assortments array within an RPN's agm_data without triggering full refetch
     */
    updateRPNAssortments: (
      state,
      action: PayloadAction<{ productLineId: number; rpnId: number; assortments: RpnAssortment[] }>
    ) => {
      const key = `${action.payload.productLineId}-${action.payload.rpnId}`;
      if (!state.rpns[key]) {
        logger.warn(`[productDetailsSlice] Attempted to update assortments for non-existent RPN: ${key}`);
        return;
      }
      if (state.rpns[key].agm_data) {
        state.rpns[key].agm_data.rpn_assortments = action.payload.assortments;
      }
    },

    /**
     * Updates only the skus array within an RPN's agm_data without triggering full refetch
     */
    updateRPNSkus: (state, action: PayloadAction<{ productLineId: number; rpnId: number; skus: Skus[] }>) => {
      const key = `${action.payload.productLineId}-${action.payload.rpnId}`;
      if (!state.rpns[key]) {
        logger.warn(`[productDetailsSlice] Attempted to update skus for non-existent RPN: ${key}`);
        return;
      }
      if (state.rpns[key].agm_data) {
        state.rpns[key].agm_data.skus = action.payload.skus;
      }
    },
  },
  extraReducers: (builder) => {
    // ========================================================================
    // fetchProductLineDetails
    // ========================================================================
    builder
      .addCase(fetchProductLineDetails.pending, (state) => {
        state.loading.productLine = true;
        state.error = null;
      })
      .addCase(fetchProductLineDetails.fulfilled, (state, action) => {
        state.loading.productLine = false;
        state.productLines[action.payload.productLineId] = action.payload.data;
      })
      .addCase(fetchProductLineDetails.rejected, (state, action) => {
        state.loading.productLine = false;
        state.error = action.payload as string;
      });

    // ========================================================================
    // fetchRPNDetails
    // ========================================================================
    builder
      .addCase(fetchRPNDetails.pending, (state) => {
        state.loading.rpn = true;
        state.error = null;
      })
      .addCase(fetchRPNDetails.fulfilled, (state, action) => {
        state.loading.rpn = false;
        const key = `${action.payload.productLineId}-${action.payload.rpnId}`;
        state.rpns[key] = action.payload.data;
      })
      .addCase(fetchRPNDetails.rejected, (state, action) => {
        state.loading.rpn = false;
        state.error = action.payload as string;
      });

    // ========================================================================
    // fetchFPNDetails
    // ========================================================================
    builder
      .addCase(fetchFPNDetails.pending, (state) => {
        state.loading.fpn = true;
        state.error = null;
      })
      .addCase(fetchFPNDetails.fulfilled, (state, action) => {
        state.loading.fpn = false;
        const key = `${action.payload.productLineId}-${action.payload.rpnId}-${action.payload.fpnId}`;
        state.fpns[key] = action.payload.data;
      })
      .addCase(fetchFPNDetails.rejected, (state, action) => {
        state.loading.fpn = false;
        state.error = action.payload as string;
      });

    // ========================================================================
    // fetchAGMVersionsAttributeOrderThunk
    // ========================================================================
    builder
      .addCase(fetchAGMVersionsAttributeOrderThunk.pending, (state) => {
        state.loading.agmVersionsAttributeOrder = true;
        state.error = null;
      })
      .addCase(fetchAGMVersionsAttributeOrderThunk.fulfilled, (state, action) => {
        state.loading.agmVersionsAttributeOrder = false;
        const key = `${action.payload.productLineId}-${action.payload.rpnId}`;
        state.agmVersionsAttributeOrder[key] = action.payload.data;
      })
      .addCase(fetchAGMVersionsAttributeOrderThunk.rejected, (state, action) => {
        state.loading.agmVersionsAttributeOrder = false;
        state.error = action.payload as string;
      });

    // ========================================================================
    // updateAGMVersionsAttributeOrderThunk
    // ========================================================================
    builder
      .addCase(updateAGMVersionsAttributeOrderThunk.pending, (state) => {
        state.loading.agmVersionsAttributeOrder = true;
        state.error = null;
      })
      .addCase(updateAGMVersionsAttributeOrderThunk.fulfilled, (state) => {
        state.loading.agmVersionsAttributeOrder = false;
        // After successful update, the caller should dispatch fetchAGMVersionsAttributeOrderThunk
        // to refresh the cached data from the server
      })
      .addCase(updateAGMVersionsAttributeOrderThunk.rejected, (state, action) => {
        state.loading.agmVersionsAttributeOrder = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setNavigationContext,
  navigateToProductLine,
  navigateToRPN,
  navigateToFPN,
  clearError,
  reset,
  updateRPNVersions,
  updateRPNAssortments,
  updateRPNSkus,
} = productDetailsSlice.actions;

export default productDetailsSlice.reducer;
